import type {
  Aircraft,
  AircraftOwnershipPeriod,
  AircraftSlg,
} from '../../types';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import {
  formatOwnershipShare,
  isPositiveOwnershipShare,
} from '../../utils/ownershipShare';

export const emptyCheck = (): AircraftSlg => ({
  last_date: null,
  next_date: null,
  overdue: false,
  days_left: null,
});

export const filesCountLabel = (count: number) => {
  const n10 = count % 10;
  const n100 = count % 100;
  if (n10 === 1 && n100 !== 11) return `${count} файл`;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return `${count} файла`;
  return `${count} файлов`;
};

export const psFilesLine = (count: number, owners: number) => {
  if (count === 0) return 'Нет';
  if (owners > 0) return `${count} из ${owners}`;
  return filesCountLabel(count);
};

export const checkLine = (item: AircraftSlg, dateFormat: DateFormatStyle) => {
  if (item.overdue) {
    return item.next_date
      ? `Просрочено с ${DateFormat(item.next_date, dateFormat)}`
      : 'Просрочено';
  }
  if (!item.last_date) return 'Не указано';
  if (item.next_date) {
    if (item.days_left != null && item.days_left <= 30) {
      return `до ${DateFormat(item.next_date, dateFormat)} (${item.days_left} дн.)`;
    }
    return `до ${DateFormat(item.next_date, dateFormat)}`;
  }
  return DateFormat(item.last_date, dateFormat);
};

export const maintenanceLabel = (aircraft: Aircraft) => {
  if (aircraft.maintenance_type === 'technician') {
    return `Техник: ${aircraft.maintenance_name || '—'}`;
  }
  if (aircraft.maintenance_type === 'organization') {
    return `Организация: ${aircraft.maintenance_name || '—'}`;
  }
  if (aircraft.maintenance_type === 'self') {
    return `Своими силами: ${aircraft.maintenance_name || '—'}`;
  }
  return '—';
};

export const currentSharePeriod = (
  periods: AircraftOwnershipPeriod[] | undefined
): AircraftOwnershipPeriod | null => {
  if (!periods || periods.length === 0) return null;
  return (
    [...periods].reverse().find((item) => !item.effective_until) ||
    periods[periods.length - 1]
  );
};

export const visibleOwnershipShares = (
  period: AircraftOwnershipPeriod | null
) =>
  (period?.shares || []).filter((row) =>
    isPositiveOwnershipShare(row.ownership_share)
  );

export const formatSharesLine = (period: AircraftOwnershipPeriod | null) => {
  const shares = visibleOwnershipShares(period);
  if (shares.length === 0) return 'Не указаны';
  return shares
    .map((row) => `${row.name} ${formatOwnershipShare(row.ownership_share)}`)
    .join(' · ');
};

export const formatShareRange = (
  period: AircraftOwnershipPeriod,
  dateFormat: DateFormatStyle
) =>
  period.effective_until
    ? `${DateFormat(period.effective_from, dateFormat)} — ${DateFormat(
        period.effective_until,
        dateFormat
      )}`
    : `с ${DateFormat(period.effective_from, dateFormat)}`;
